import * as THREE from "three";
import { COLOR_HEX, type Color } from "@core/colors";
import { makeRoundedRect, setupOrtho, tween } from "@core/three-helpers";
import { loadLevels, getLevel, nextLevelIndex, levelNumber } from "@core/level-loader";
import { levels as levelData } from "./levels";

const canvas = document.getElementById("game") as HTMLCanvasElement;
const hudLevel = document.getElementById("hud-level")!;
const hudStatus = document.getElementById("hud-status")!;
const banner = document.getElementById("banner")!;

const { scene, camera, renderer, worldFromEvent } = setupOrtho(canvas, {
  virtualHeight: 22,
  background: 0x1b1428,
});

const PIECES_PER_BAND = 9;
const BOX_CAPACITY = 3;
const BOXES_COUNT = 4;
const CONVEYOR_CELLS = 12;

interface BandState {
  id: number;
  color: Color;
  group: THREE.Group;
  row: number;
  col: number;
  released: boolean;
}

interface ConveyorPiece {
  color: Color;
  mesh: THREE.Mesh;
  cellIndex: number;
  landed: boolean;
}

interface BoxState {
  color: Color;
  group: THREE.Group;
  pieces: number;
  fillMesh: THREE.Mesh;
}

const bands: BandState[] = [];
const conveyor: (ConveyorPiece | null)[] = new Array(CONVEYOR_CELLS).fill(null);
let boxes: BoxState[] = [];
let nextBandId = 1;
let busy = false;

// Layout constants (world units)
const FIELD_BOTTOM_Y = 1.5;
const SHREDDER_Y = 0.0;
const CONVEYOR_Y = -2.2;
const BOXES_Y = -5.6;
const BAND_WIDTH = 4;
const BAND_HEIGHT = 0.75;
const PIN_RADIUS = 0.35;

function makeBand(color: Color, row: number, col: number, totalCols: number): BandState {
  const group = new THREE.Group();
  const colSpan = 6;
  const colStep = colSpan / Math.max(1, totalCols - 1);
  const x = totalCols === 1 ? 0 : -colSpan / 2 + col * colStep;
  const y = FIELD_BOTTOM_Y + row * 1.5;
  group.position.set(x, y, 0);

  const band = makeRoundedRect(BAND_WIDTH, BAND_HEIGHT, BAND_HEIGHT / 2, COLOR_HEX[color]);
  band.position.z = 0.1;
  group.add(band);

  const pinMat = new THREE.MeshBasicMaterial({ color: 0xece6ff });
  const pinGeom = new THREE.CircleGeometry(PIN_RADIUS, 24);
  const leftPin = new THREE.Mesh(pinGeom, pinMat);
  leftPin.position.set(-BAND_WIDTH / 2, 0, 0.2);
  const rightPin = new THREE.Mesh(pinGeom, pinMat);
  rightPin.position.set(BAND_WIDTH / 2, 0, 0.2);
  group.add(leftPin, rightPin);

  scene.add(group);
  const id = nextBandId++;
  return { id, color, group, row, col, released: false };
}

function isBandBlocked(b: BandState): boolean {
  // Blocked if any not-yet-released band lives in a row above this band
  // and overlaps its x-range (here we keep it simple: same column-stack).
  return bands.some(
    (other) =>
      other !== b &&
      !other.released &&
      other.row > b.row &&
      Math.abs(other.group.position.x - b.group.position.x) < BAND_WIDTH * 0.5,
  );
}

function makeBoxes(palette: Color[]) {
  const boxWidth = 1.6;
  const boxGap = 0.3;
  const totalWidth = BOXES_COUNT * boxWidth + (BOXES_COUNT - 1) * boxGap;
  const startX = -totalWidth / 2 + boxWidth / 2;

  for (let i = 0; i < BOXES_COUNT; i++) {
    const color = palette[i % palette.length];
    const group = new THREE.Group();
    group.position.set(startX + i * (boxWidth + boxGap), BOXES_Y, 0);

    const back = makeRoundedRect(boxWidth, boxWidth, 0.25, 0x2a1f3a);
    back.position.z = 0;
    group.add(back);

    const rim = makeRoundedRect(boxWidth * 0.92, boxWidth * 0.32, 0.12, COLOR_HEX[color]);
    rim.position.set(0, boxWidth * 0.3, 0.1);
    group.add(rim);

    const fill = makeRoundedRect(boxWidth * 0.84, 0.02, 0.05, COLOR_HEX[color]);
    fill.position.set(0, -boxWidth * 0.36, 0.15);
    fill.scale.y = 0.001;
    group.add(fill);

    scene.add(group);
    boxes.push({ color, group, pieces: 0, fillMesh: fill });
  }
}

function makeShredder() {
  const shredderWidth = 8;
  const shredderHeight = 0.9;
  const body = makeRoundedRect(shredderWidth, shredderHeight, 0.25, 0x3a2a4f);
  body.position.set(0, SHREDDER_Y, -0.1);
  scene.add(body);

  // Teeth
  const teethCount = 12;
  const teethGeom = new THREE.PlaneGeometry(0.45, 0.6);
  const teethMat = new THREE.MeshBasicMaterial({ color: 0x6b4a8a });
  for (let i = 0; i < teethCount; i++) {
    const t = new THREE.Mesh(teethGeom, teethMat);
    t.position.set(-shredderWidth / 2 + 0.55 + i * 0.62, SHREDDER_Y + 0.45, 0);
    scene.add(t);
  }

  // Label
  const labelMat = new THREE.MeshBasicMaterial({ color: 0xece6ff, opacity: 0.0 });
  const labelGeom = new THREE.PlaneGeometry(0, 0);
  const label = new THREE.Mesh(labelGeom, labelMat);
  scene.add(label);
}

function makeConveyor() {
  const cellWidth = 0.8;
  const totalWidth = CONVEYOR_CELLS * cellWidth + (CONVEYOR_CELLS - 1) * 0.05;
  const startX = -totalWidth / 2 + cellWidth / 2;
  const back = makeRoundedRect(totalWidth + 0.6, 0.95, 0.3, 0x2a1f3a);
  back.position.set(0, CONVEYOR_Y, -0.1);
  scene.add(back);

  for (let i = 0; i < CONVEYOR_CELLS; i++) {
    const cell = makeRoundedRect(cellWidth - 0.05, 0.7, 0.15, 0x14101e);
    cell.position.set(startX + i * (cellWidth + 0.05), CONVEYOR_Y, 0);
    scene.add(cell);
  }
}

function conveyorWorldX(idx: number): number {
  const cellWidth = 0.8;
  const totalWidth = CONVEYOR_CELLS * cellWidth + (CONVEYOR_CELLS - 1) * 0.05;
  const startX = -totalWidth / 2 + cellWidth / 2;
  return startX + idx * (cellWidth + 0.05);
}

const allLevels = loadLevels(levelData);

function spawnLevel(levelIdx: number) {
  // Wipe state
  for (const b of bands) scene.remove(b.group);
  bands.length = 0;
  for (const b of boxes) scene.remove(b.group);
  boxes = [];
  for (let i = 0; i < conveyor.length; i++) {
    const p = conveyor[i];
    if (p) scene.remove(p.mesh);
    conveyor[i] = null;
  }

  makeShredder();
  makeConveyor();

  const cfg = getLevel(allLevels, levelIdx);
  makeBoxes(cfg.palette);

  for (const spec of cfg.bands) {
    bands.push(makeBand(spec.color, spec.row, spec.col, cfg.cols));
  }

  hudLevel.textContent = `Level ${levelNumber(levelIdx)}`;
  hudStatus.textContent = "Tap an unblocked band";
  busy = false;
}

function showBanner(text: string, durationMs: number) {
  banner.textContent = text;
  banner.classList.add("show");
  setTimeout(() => banner.classList.remove("show"), durationMs);
}

let currentLevelIdx = 0;

function onPointerDown(ev: PointerEvent) {
  if (busy) return;
  const world = worldFromEvent(ev);

  for (const b of bands) {
    if (b.released) continue;
    const dx = Math.abs(world.x - b.group.position.x);
    const dy = Math.abs(world.y - b.group.position.y);
    if (dx <= BAND_WIDTH / 2 + 0.3 && dy <= BAND_HEIGHT) {
      if (isBandBlocked(b)) {
        flashBlocked(b);
        return;
      }
      releaseBand(b);
      return;
    }
  }
}

function flashBlocked(b: BandState) {
  hudStatus.textContent = "Blocked — pick a higher band first";
  const originalColor = (b.group.children[0] as THREE.Mesh).material as THREE.MeshBasicMaterial;
  const old = originalColor.color.getHex();
  originalColor.color.setHex(0xff4d4d);
  setTimeout(() => originalColor.color.setHex(old), 220);
}

function releaseBand(b: BandState) {
  b.released = true;
  hudStatus.textContent = "Shredding…";
  tween(b.group, { y: SHREDDER_Y + 0.3 }, 360, () => {
    // Hide band
    scene.remove(b.group);
    // Spawn 9 pieces
    spawnPieces(b.color);
  });
}

function spawnPieces(color: Color) {
  // 9 little pieces fly out of the shredder along an arc into the conveyor
  let landed = 0;
  for (let i = 0; i < PIECES_PER_BAND; i++) {
    const geom = new THREE.CircleGeometry(0.18, 16);
    const mat = new THREE.MeshBasicMaterial({ color: COLOR_HEX[color] });
    const piece = new THREE.Mesh(geom, mat);
    const startX = (Math.random() - 0.5) * 1.5;
    piece.position.set(startX, SHREDDER_Y - 0.4, 0.4);
    scene.add(piece);

    const delay = i * 60;
    setTimeout(() => {
      const targetIdx = nextFreeConveyorCell();
      if (targetIdx < 0) {
        // overflow → conveyor full
        scene.remove(piece);
        hudStatus.textContent = "Conveyor jammed — restart";
        flashBanner();
        return;
      }
      const tx = conveyorWorldX(targetIdx);
      const ty = CONVEYOR_Y;
      const conveyorPiece: ConveyorPiece = {
        color,
        mesh: piece,
        cellIndex: targetIdx,
        landed: false,
      };
      conveyor[targetIdx] = conveyorPiece;
      tween(piece, { x: tx, y: ty }, 380 + Math.random() * 120, () => {
        conveyorPiece.landed = true;
        landed++;
        autoFeedBoxes();
        if (landed === PIECES_PER_BAND) {
          hudStatus.textContent =
            bands.every((b) => b.released) ? "All bands shred — feeding…" : "Tap an unblocked band";
        }
      });
    }, delay);
  }
}

function nextFreeConveyorCell(): number {
  return conveyor.findIndex((c) => c === null);
}

function autoFeedBoxes() {
  // For each piece on conveyor (already landed), if a box of matching color has space,
  // fly piece into it. In-flight pieces are skipped to avoid leaking their slot.
  for (let i = 0; i < conveyor.length; i++) {
    const p = conveyor[i];
    if (!p || !p.landed) continue;
    const targetBox = boxes.find((b) => b.color === p.color && !boxFull(b));
    if (!targetBox) continue;
    conveyor[i] = null;
    const tx = targetBox.group.position.x;
    const ty = targetBox.group.position.y;
    tween(p.mesh, { x: tx, y: ty + 0.05 }, 320, () => {
      scene.remove(p.mesh);
      targetBox.pieces++;
      const progress = targetBox.pieces / BOX_CAPACITY;
      targetBox.fillMesh.scale.y = Math.max(0.001, progress * 24);
      if (boxFull(targetBox)) {
        flyAwayBox(targetBox);
      }
      maybeWin();
    });
  }
}

function boxFull(b: BoxState): boolean {
  return b.pieces >= BOX_CAPACITY;
}

function flyAwayBox(b: BoxState) {
  tween(b.group, { y: b.group.position.y + 10, sx: 0.2, sy: 0.2 }, 500, () => {
    scene.remove(b.group);
    const newColor = pickReplacementColor();
    if (!newColor) {
      // No more pieces needing boxes — level is fully consumed
      boxes = boxes.filter((bb) => bb !== b);
      maybeWin();
      return;
    }
    const reborn = createBoxAt(b.group.position.x, BOXES_Y + 8, newColor);
    boxes = boxes.map((bb) => (bb === b ? reborn : bb));
    tween(reborn.group, { y: BOXES_Y }, 360, () => {
      autoFeedBoxes();
    });
  });
}

function pickReplacementColor(): Color | null {
  // Prioritize draining the conveyor: pick whichever color has the most
  // orphaned pieces currently waiting. Fall back to unreleased band colors.
  const counts = new Map<Color, number>();
  for (const c of conveyor) {
    if (c) counts.set(c.color, (counts.get(c.color) ?? 0) + 1);
  }
  for (const band of bands) {
    if (band.released) continue;
    counts.set(band.color, (counts.get(band.color) ?? 0) + PIECES_PER_BAND);
  }
  if (counts.size === 0) return null;
  let best: Color | null = null;
  let bestCount = -1;
  for (const [c, n] of counts) {
    if (n > bestCount) {
      best = c;
      bestCount = n;
    }
  }
  return best;
}

function createBoxAt(x: number, y: number, color: Color): BoxState {
  const boxWidth = 1.6;
  const group = new THREE.Group();
  group.position.set(x, y, 0);
  const back = makeRoundedRect(boxWidth, boxWidth, 0.25, 0x2a1f3a);
  group.add(back);
  const rim = makeRoundedRect(boxWidth * 0.92, boxWidth * 0.32, 0.12, COLOR_HEX[color]);
  rim.position.set(0, boxWidth * 0.3, 0.1);
  group.add(rim);
  const fill = makeRoundedRect(boxWidth * 0.84, 0.02, 0.05, COLOR_HEX[color]);
  fill.position.set(0, -boxWidth * 0.36, 0.15);
  fill.scale.y = 0.001;
  group.add(fill);
  scene.add(group);
  return { color, group, pieces: 0, fillMesh: fill };
}

function maybeWin() {
  const allBandsReleased = bands.every((b) => b.released);
  const conveyorEmpty = conveyor.every((c) => c === null);
  if (allBandsReleased && conveyorEmpty) {
    showBanner(`Level ${levelNumber(currentLevelIdx)} clear!`, 1800);
    busy = true;
    setTimeout(() => {
      currentLevelIdx = nextLevelIndex(allLevels, currentLevelIdx);
      spawnLevel(currentLevelIdx);
    }, 1700);
  }
}

function flashBanner() {
  showBanner("Conveyor jammed — tap menu to retry", 2000);
}

canvas.addEventListener("pointerdown", onPointerDown);

spawnLevel(currentLevelIdx);

function loop() {
  requestAnimationFrame(loop);
  renderer.render(scene, camera);
}
loop();

// Bind for debugging in console
(window as unknown as { __proto: unknown }).__proto = {
  bands,
  conveyor,
  boxes,
};
